import React from 'react';
import isEmpty from 'lodash.isempty';
import { Box, Typography } from '@trp-individual-investor-adv-svc/ii-prosp-components';

import api from '../../actions/EndPoints';
import axiosInstance from '../../actions/AxiosInstance';
import connectWithUserStore from '../../reducers/connectWithUserStore';
import { isValidPinNumber } from '../shared/Validation';
import { commonErrorPromise, log, showModal } from '../../Utils';
import HubPageBase from '../HubPages/HubPageBase';

import { PsBeaconButton, PsBeaconControl } from '../shared/BeaconRegister';
import LoadingSpinner from '../../containers/LoadingSpinner';
import { Navigate, useNavigate } from 'react-router';
import { USER_PROFILE_FORM_MODAL } from '../../Constants';
import UserProfileForm from '../modals/UserProfileForm';

const PinValidation = connectWithUserStore(({ state, dispatch, profiler }) => {
    const [wrongPin, setWrongPin] = React.useState(false);
    const [pin, setPin] = React.useState('');
    const [inProgress, setPinSubmitInProgress] = React.useState(false);
    const [fiveAttemptsError, setPinAttemptsError] = React.useState(false);
    const { apiKey, archivedGroupSummary } = state;
    const encodedUrlText = JSON.parse(sessionStorage.getItem('emailInv'))
        ? JSON.parse(sessionStorage.getItem('emailInv')).encodedUrlText
        : null;
    const getEncodedUrlText = encodedUrlText ?? state?.pendingInvite?.encodedUrlText;
    const limbo = archivedGroupSummary && archivedGroupSummary.archivedGroupsCount > 0;
    const isLimbo = limbo && state?.planningGroup === null;
    const hasActiveConsent = !isEmpty(state?.currentClientData?.activeConsent);
    const pinControl = document.querySelectorAll(`[id="pin-entry-page-control"]`)[0];
    const navigate = useNavigate();

    const redirectToConsent = (engagementType) => {
        if (!engagementType || engagementType === 'RAS-Onboarding') {
            navigate('/co-client-consent');
        } else {
            sessionStorage.setItem('joiningRasHH', true);
            navigate('/account-selection');
        }
    };

    if (
        (!getEncodedUrlText || ((hasActiveConsent || state.isPrimary) && !isLimbo)) &&
        state?.currentClientData !== null &&
        !isEmpty(state?.currentClientData) &&
        state?.currentClientData.clientType === null
    ) {
        log(console.log('encodedUrlText not eligible for pin entry', encodedUrlText));
        log(
            console.log(
                'Redirected from Pin Entry page',
                '\n',
                'Consent Expiration Date: ',
                state.currentClientData,
                '\n',
                'Is Primary Client: ',
                state.isPrimary
            )
        );
        navigate(profiler?.route ?? '/');
    }

    React.useEffect(() => {
        if (isValidPinNumber(pin)) {
            pinControl.removeAttribute('invalid', '');
        }
    }, [pin]);

    React.useEffect(() => {
        if (state?.userStatusFailure) {
            commonErrorPromise();
        }
    }, [state?.userStatusFailure]);

    log(console.log('encodedUrlText pin entry', encodedUrlText));
    const handleReset = () => {
        setPin('');
    };

    const validatePin = () => {
        setPinSubmitInProgress(true);
        async function statusConseted(_key_) {
            try {
                const pinAPI = await axiosInstance.post(
                    api.pinValidationAPI,
                    {
                        encodedUrlText,
                        invitePinNumber: pin,
                    },
                    {
                        headers: {
                            apikey: `${_key_}`,
                        },
                    }
                );

                if (pinAPI) {
                    dispatch({
                        type: 'ENTERED_PIN_CO_CLIENT',
                        hasEnteredPin: true,
                        engagementType: pinAPI.data?.engagementType,
                        pinRecievedCoClientData: pinAPI.data,
                        isPrimary: false,
                    });
                    setPinSubmitInProgress(false);
                    setTimeout(() => {
                        sessionStorage.setItem('invitePinNumber', pin);
                        handleReset();
                    }, 2000);

                    // Check if user is a Financial Planning client and interventionRequired flag to see if the User Profile Form needs to be shown.
                    if (!state?.currentClientData && state?.userStatus?.interventionRequired) {
                        showModal(USER_PROFILE_FORM_MODAL);
                    } else {
                        redirectToConsent(pinAPI.data?.engagementType);
                    }
                }
            } catch (error) {
                setPinSubmitInProgress(false);
                if (error.response) {
                    if (
                        error.response.data.httpStatusCode === 403 ||
                        error.response.data.httpStatusCode === 400
                    ) {
                        setPinSubmitInProgress(false);
                        pinControl.setAttribute('invalid', '');
                        setWrongPin(true);
                    } else if (error.response.data.httpStatusCode === 401) {
                        setPinAttemptsError(true);
                    }
                }
            }
        }
        if (!isValidPinNumber(pin)) {
            Promise.resolve(setPinSubmitInProgress(false))
                .then(() => setWrongPin(false))
                .then(() => pinControl.setAttribute('invalid', ''));
        }
        if (isValidPinNumber(pin) && !inProgress) {
            statusConseted(apiKey);
        }
    };

    return !fiveAttemptsError ? (
        <>
            <HubPageBase keepInv noInvModal state={state} dispatch={dispatch}>
                <Box
                    className="b-container-content"
                    sx={{ inlineSize: 'unset !important', paddingTop: '64px' }}>
                    <Typography
                        sx={{ paddingBottom: '16px' }}
                        type="headline" serif loose scale={400}>
                        Enter your secure PIN
                    </Typography>
                    <Typography
                        loose light
                        sx={{ paddingBottom: '32px' }}>
                        Your co-client created a secure 4-digit PIN. If you do not have the PIN, ask
                        them to share it.
                    </Typography>
                    <Box sx={{ maxWidth: { xs: '100%', sm: '450px' }, paddingBottom: '32px' }}>
                        <PsBeaconControl id="pin-entry-page-control" className="b-reset">
                            <label slot="label" htmlFor="partner-Pin">
                                Enter 4-digit PIN number
                            </label>
                            <input
                                type="text"
                                name="partnerPin"
                                id="partner-pin"
                                data-testid="partner-pin"
                                aria-label="partner-pin"
                                defaultValue={pin}
                                required
                                onChange={(e) => setPin(e.target.value)}
                                minLength="4"
                                maxLength="4"
                            />
                            <div slot="hint">For security, you need a PIN to continue.</div>
                            <div slot="error">
                                {wrongPin
                                    ? 'This PIN does not match our records. Please try again.'
                                    : 'The PIN must be a 4-digit number. Please try again.'}
                            </div>
                        </PsBeaconControl>
                    </Box>
                    <PsBeaconButton
                        onClick={validatePin}
                        scale="jumbo"
                        data-testid="continue-beacon-button">
                        {!inProgress && 'Continue'}
                        {inProgress && <LoadingSpinner scale="mini" text="Loading..." />}
                    </PsBeaconButton>
                </Box>
            </HubPageBase>
            <UserProfileForm state={state} dispatch={dispatch} />
        </>
    ) : (
        <Navigate to="/pin-max-attempts" />
    );
});

export default function PinValidationView() {
    return <PinValidation />;
}
